import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from '@/lib/router';
import type { Session, Subscription } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { redirectToCheckout } from '../lib/stripe';
import { btnBase, btnPrimary } from '@/lib/ui';
import { cn } from '@/lib/cn';
import {
  authFailureMessage,
  buildAuthEntryPath,
  safeAuthNext,
} from '@/lib/auth/recovery';

type CallbackPhase =
  | { status: 'loading'; message: string }
  | {
      status: 'failed';
      message: string;
      retryable: boolean;
      kind: 'link' | 'checkout';
    };

/**
 * Completes email-confirmation and password-recovery links. Provider errors
 * stay on this route with actionable, privacy-safe recovery; they never leave
 * the user on an indefinite spinner.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flow =
    searchParams.get('flow') === 'recovery' ? 'recovery' : 'confirmation';
  const plan = searchParams.get('plan') === 'pro' ? 'pro' : 'free';
  const next = safeAuthNext(searchParams.get('next'));
  const code = searchParams.get('code');
  const providerRejected = Boolean(
    searchParams.get('error') || searchParams.get('error_code'),
  );

  const [phase, setPhase] = useState<CallbackPhase>({
    status: 'loading',
    message:
      flow === 'recovery'
        ? 'Opening your secure password reset…'
        : 'Confirming your account…',
  });
  const attemptRef = useRef(0);
  const lockedRef = useRef(false);
  const initialStartedRef = useRef(false);
  const exchangedCodeRef = useRef(false);
  const sessionRef = useRef<Session | null>(null);
  const failureHeadingRef = useRef<HTMLHeadingElement>(null);

  const complete = useCallback(async () => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    const attempt = ++attemptRef.current;
    setPhase({
      status: 'loading',
      message:
        flow === 'recovery'
          ? 'Opening your secure password reset…'
          : 'Confirming your account…',
    });

    if (providerRejected) {
      setPhase({
        status: 'failed',
        message: authFailureMessage('callback', null),
        retryable: false,
        kind: 'link',
      });
      lockedRef.current = false;
      return;
    }

    let checkoutStarted = false;
    try {
      if (code && !exchangedCodeRef.current) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (attemptRef.current !== attempt) return;
        if (error) throw error;
        exchangedCodeRef.current = true;
      }

      const session = sessionRef.current ?? (await resolveSession());
      if (attemptRef.current !== attempt) return;
      if (!session) throw new Error('session unavailable');
      sessionRef.current = session;

      if (flow === 'recovery') {
        navigate(
          buildAuthEntryPath({
            mode: 'reset',
            next,
          }),
          { replace: true },
        );
        return;
      }

      if (plan === 'pro') {
        checkoutStarted = true;
        setPhase({
          status: 'loading',
          message: 'Account confirmed — taking you to checkout…',
        });
        await redirectToCheckout(session.user.id, session.access_token);
        return;
      }

      navigate(next ?? '/account', { replace: true });
    } catch {
      if (attemptRef.current !== attempt) return;
      setPhase({
        status: 'failed',
        message: authFailureMessage(
          checkoutStarted ? 'checkout' : 'callback',
          null,
        ),
        retryable: true,
        kind: checkoutStarted ? 'checkout' : 'link',
      });
    } finally {
      if (attemptRef.current === attempt) lockedRef.current = false;
    }
  }, [code, flow, navigate, next, plan, providerRejected]);

  useEffect(() => {
    // The ref survives StrictMode's effect replay, so a one-shot PKCE code is
    // never exchanged twice. Explicit retry calls `complete` directly.
    if (initialStartedRef.current) return;
    initialStartedRef.current = true;
    void complete();
  }, [complete]);

  useEffect(() => {
    if (phase.status === 'failed') failureHeadingRef.current?.focus();
  }, [phase.status]);

  if (phase.status === 'failed') {
    const primaryPath =
      phase.kind === 'checkout'
        ? '/account'
        : flow === 'recovery'
        ? buildAuthEntryPath({
            mode: 'forgot',
            error: 'recovery_failed',
            next,
          })
        : buildAuthEntryPath({
            mode: 'signin',
            error: 'confirmation_failed',
            plan,
            next,
          });

    return (
      <main className="flex min-h-screen w-full items-center justify-center px-4 py-8">
        <section className="w-full max-w-[440px] min-w-0 rounded-lg border border-border bg-surface-white p-5 shadow-sm">
          <h1
            ref={failureHeadingRef}
            tabIndex={-1}
            className="m-0 font-ui text-lg font-semibold text-text-primary outline-none"
          >
            {phase.kind === 'checkout'
              ? 'Checkout unavailable'
              : flow === 'recovery'
              ? 'Password-reset link unavailable'
              : 'Confirmation link unavailable'}
          </h1>
          <p
            className="mt-3 mb-0 font-ui text-sm leading-relaxed text-error-text break-words [overflow-wrap:anywhere]"
            role="alert"
          >
            {phase.message}
          </p>
          <div className="mt-5 flex flex-col gap-2">
            {phase.retryable && (
              <button
                type="button"
                onClick={() => void complete()}
                className={cn(btnBase, btnPrimary, 'w-full')}
              >
                {phase.kind === 'checkout'
                  ? 'Try checkout again'
                  : 'Try secure link again'}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(primaryPath, { replace: true })}
              className={cn(
                btnBase,
                phase.retryable
                  ? 'w-full bg-transparent text-text-secondary hover:bg-surface-1'
                  : cn(btnPrimary, 'w-full'),
              )}
            >
              {flow === 'recovery'
                ? 'Request a new reset link'
                : phase.kind === 'checkout'
                  ? 'Continue to account'
                  : 'Return to sign in'}
            </button>
            {flow === 'confirmation' && phase.kind !== 'checkout' && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    buildAuthEntryPath({
                      mode: 'signup',
                      error: 'confirmation_failed',
                      plan,
                      next,
                    }),
                    { replace: true },
                  )
                }
                className={cn(
                  btnBase,
                  'w-full bg-transparent text-text-secondary hover:bg-surface-1',
                )}
              >
                Create account again
              </button>
            )}
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center"
      aria-busy="true"
    >
      <div
        className="w-10 h-10 border-4 border-border border-t-navy-500 rounded-full animate-cell-spin"
        aria-hidden="true"
      />
      <p
        className="m-0 max-w-full font-ui text-sm text-text-tertiary break-words [overflow-wrap:anywhere]"
        role="status"
      >
        {phase.message}
      </p>
    </main>
  );
}

/**
 * Tolerates the detectSessionInUrl race and always settles. The subscription
 * is cleaned up even when an auth event fires synchronously during setup.
 */
export function resolveSession(timeoutMs = 6000): Promise<Session | null> {
  return new Promise((resolve) => {
    let settled = false;
    let subscription: Subscription | null = null;

    const finish = (session: Session | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      subscription?.unsubscribe();
      resolve(session);
    };

    const timer = window.setTimeout(() => finish(null), timeoutMs);
    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) finish(session);
    });
    subscription = authSubscription;
    if (settled) subscription.unsubscribe();

    void supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (session) finish(session);
      })
      .catch(() => finish(null));
  });
}
